import { get, patch, post, put } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { RequestStatus, SupportRequest } from '../types';
import {
  SupportRequestMessageApi,
  SupportRequestApi,
  SupportAttachmentsResponseApi,
  SupportRequestCategoriesApi,
  SupportRequestCategoriesElementsApi,
  SupportRequestCategoriesListApi,
  SupportRequestCategoryUpdateApi,
  SupportRequestsPageApi,
  SupportRequestStatusApi,
  SupportRequestStatusUpdateApi,
} from '../SupportRequests/apiTypes';
import { ActivityMessageListResponse } from '../ActivityPanel/types';
import {
  mapSupportAttachments,
  mapSupportRequest,
  mapSupportRequestStatusToApi,
} from '../SupportRequests/mappers';
import { mapSupportStatusToApi } from '../SupportRequests/status';

export interface UseSupportRequestsOptions {
  pageSize?: number;
  page?: number;
  filters?: Array<{
    field: string;
    value: string | string[];
  }>;
  search?: string;
  sort?: Array<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>;
}

export interface UseSupportRequestLogsOptions {
  supportRequestId?: string;
  pageSize?: number;
  page?: number;
  types?: string[];
}

export interface UseSupportRequestAttachmentsOptions {
  supportRequestId?: string;
}

interface SupportRequestsPage {
  elements: SupportRequest[];
  total: number;
  totalPages: number;
  page: number;
  isFirst: boolean;
  isLast: boolean;
}

interface SupportRequestsFilter {
  requestId?: string;
  status?: SupportRequestStatusApi[];
}

interface SupportRequestsPayload {
  page: number;
  limit: number;
  sort: Array<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>;
  filter: SupportRequestsFilter;
}

interface SupportRequestLogFilter {
  supportRequestId: string;
  types?: string[];
}

interface SupportRequestLogPayload {
  page: number;
  limit: number;
  sort: Array<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>;
  filter: SupportRequestLogFilter;
}

// temporary function to build the filter for the support requests
// TODO: remove this function when the filter is implemented in the backend
export function buildSupportFilter(
  filters: UseSupportRequestsOptions['filters'],
): SupportRequestsFilter {
  if (!filters || filters.length === 0) return {};

  const filter = filters[0];
  if (filter.field === 'status') {
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    const mappedStatuses = values
      .map(value => mapSupportStatusToApi(value))
      .filter((value): value is SupportRequestStatusApi => Boolean(value));
    return mappedStatuses.length > 0 ? { status: mappedStatuses } : {};
  }
  return {
    [filter.field]: filter.value,
  };
}

export function mapSortFieldToBackend(
  field: string,
  direction: 'ASC' | 'DESC',
): Array<{ field: string; direction: 'ASC' | 'DESC' }> {
  if (field === 'name') {
    return [
      { field: 'firstName', direction },
      { field: 'lastName', direction },
    ];
  }

  if (field === 'submittedAt') {
    return [{ field: 'createdDate', direction }];
  }

  const fieldMapping: Record<string, string> = {
    requestId: 'requestId',
    submittedAt: 'submittedAt',
    assignedAdmin: 'assignedAdminEmail',
    status: 'status',
    category: 'category',
    description: 'description',
    resolutionComment: 'statusReason',
  };

  const backendField = fieldMapping[field] || field;
  return [{ field: backendField, direction }];
}

function mapSupportPage(api: SupportRequestsPageApi): SupportRequestsPage {
  return {
    ...api,
    elements: api.elements.map(mapSupportRequest),
  };
}

export function useSupportRequests({
  pageSize = 10,
  page = 0,
  filters = [],
  search = '',
  sort = [],
}: UseSupportRequestsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['support-requests', { pageSize, page, filters, search, sort }],
    queryFn: async () => {
      const idToken = await getIdToken();

      const payload: SupportRequestsPayload = {
        page,
        limit: pageSize,
        sort,
        filter: buildSupportFilter(filters),
      };

      const response = await post<SupportRequestsPageApi>(
        '/requests/support/page',
        payload,
        apiUrl,
        idToken,
      );

      return mapSupportPage(response);
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useSupportRequest(id?: string) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['support-request', { id }],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!id) throw new Error('No request id provided');
      const response = await get<SupportRequestApi>(
        `/requests/support/${id}`,
        apiUrl,
        idToken,
      );
      return mapSupportRequest(response);
    },
    enabled: isSignedIn && !!id,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSupportRequestStatus() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: RequestStatus;
      reason?: string;
    }) => {
      const idToken = await getIdToken();
      const payload: SupportRequestStatusUpdateApi = {
        status: mapSupportRequestStatusToApi(status),
        statusReason: reason,
      };
      return await put<SupportRequest>(
        `/requests/support/${id}/status`,
        payload,
        apiUrl,
        idToken,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
      queryClient.invalidateQueries({
        queryKey: ['support-request', { id: variables.id }],
      });
    },
  });
}

export function useSupportRequestCategories() {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['support-request-categories'],
    queryFn: async () => {
      const idToken = await getIdToken();

      const response = await get<
        | SupportRequestCategoriesApi
        | SupportRequestCategoriesElementsApi
        | SupportRequestCategoriesListApi
      >('/requests/support/categories', apiUrl, idToken);

      if (Array.isArray(response)) return response;
      if ('elements' in response) return response.elements || [];
      return response.categories || [];
    },
    enabled: isSignedIn,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSupportRequestCategory() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, category }: { id: string; category: string }) => {
      const idToken = await getIdToken();

      const payload: SupportRequestCategoryUpdateApi = { category };
      return await patch<void>(
        `/requests/support/${id}/category`,
        payload,
        apiUrl,
        idToken,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
      queryClient.invalidateQueries({
        queryKey: ['support-request', { id: variables.id }],
      });
    },
  });
}

export function useSendSupportMessage() {
  const { getIdToken, apiUrl } = useApiEssentials();

  return useMutation({
    mutationFn: async ({
      id,
      email,
      message,
    }: {
      id: string;
      email: string;
      message: string;
    }) => {
      const idToken = await getIdToken();
      const payload: SupportRequestMessageApi = { email, message };
      return await post<void>(
        `/requests/support/${id}/message`,
        payload,
        apiUrl,
        idToken,
      );
    },
  });
}

export function useSupportRequestLogs({
  supportRequestId,
  pageSize = 50,
  page = 0,
  types = [],
}: UseSupportRequestLogsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: [
      'support-request-logs',
      { supportRequestId, pageSize, page, types },
    ],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!supportRequestId) throw new Error('No support request id provided');

      const filter: SupportRequestLogFilter = { supportRequestId };
      if (types.length > 0) filter.types = types;

      const payload: SupportRequestLogPayload = {
        page,
        limit: pageSize,
        sort: [{ field: 'createdDate', direction: 'DESC' }],
        filter,
      };

      const response = await post<ActivityMessageListResponse>(
        '/requests/support/log/page',
        payload,
        apiUrl,
        idToken,
      );

      return response;
    },
    enabled: isSignedIn && !!supportRequestId,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useSupportRequestAttachments({
  supportRequestId,
}: UseSupportRequestAttachmentsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['support-request-attachments', { supportRequestId }],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!supportRequestId) throw new Error('No support request id provided');

      const response = await get<SupportAttachmentsResponseApi>(
        `/requests/support/${supportRequestId}/attachments`,
        apiUrl,
        idToken,
      );

      return mapSupportAttachments(response.elements);
    },
    enabled: isSignedIn && !!supportRequestId,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
